import React from "react"
import { View, StyleSheet, Text, TouchableOpacity } from "react-native"
import { theme } from "@common/theme/theme"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import {
    BREVET_MODULES_BY_SUBJECT,
    BREVET_SUBJECTS,
} from "../constants/brevetModules"

/**
 * Affiche les modules par matière.
 * - `subjects` array d'ids de matières
 * - `title` titre du bloc
 * - `selectedModules` array d'ids de modules cochés (si fourni → mode sélectionnable)
 * - `onToggleModule` callback (moduleId) => void (si fourni → mode sélectionnable)
 */
const BrevetModulesList = ({
    subjects = [],
    title,
    selectedModules,
    onToggleModule,
    style,
}) => {
    if (!subjects.length) return null
    const selectable = !!onToggleModule

    return (
        <View style={[styles.container, style]}>
            {title && <Text style={styles.title}>{title}</Text>}
            {subjects.map((subjectId) => {
                const subject = BREVET_SUBJECTS.find((s) => s.id === subjectId)
                const modules = BREVET_MODULES_BY_SUBJECT[subjectId] || []
                if (!subject || !modules.length) return null
                return (
                    <View key={subjectId} style={{ marginBottom: 10 }}>
                        <Text style={styles.subjectLabel}>{subject.label}</Text>
                        <View style={styles.chipsRow}>
                            {modules.map((m) => {
                                const isSelected =
                                    !selectable ||
                                    (selectedModules || []).includes(m.id)
                                return (
                                    <TouchableOpacity
                                        key={m.id}
                                        onPress={
                                            selectable
                                                ? () => onToggleModule(m.id)
                                                : undefined
                                        }
                                        activeOpacity={selectable ? 0.7 : 1}
                                        style={[
                                            styles.moduleChip,
                                            selectable &&
                                                isSelected &&
                                                styles.moduleChipSelected,
                                            selectable &&
                                                !isSelected &&
                                                styles.moduleChipUnselected,
                                        ]}
                                    >
                                        {selectable && (
                                            <FontAwesome5
                                                name={isSelected ? "check" : "plus"}
                                                size={9}
                                                color={
                                                    isSelected
                                                        ? theme.colors.primary
                                                        : theme.colors.light
                                                }
                                                style={{ marginRight: 5 }}
                                            />
                                        )}
                                        <Text
                                            style={[
                                                styles.moduleLabel,
                                                selectable &&
                                                    !isSelected &&
                                                    styles.moduleLabelUnselected,
                                            ]}
                                        >
                                            {m.label}
                                        </Text>
                                    </TouchableOpacity>
                                )
                            })}
                        </View>
                    </View>
                )
            })}
        </View>
    )
}

/**
 * Variante "flat" : juste une liste de chips de modules (pour l'écran reco).
 */
export const BrevetModulesChipFlat = ({ modules = [], style }) => {
    if (!modules.length) return null
    return (
        <View style={[styles.chipsRow, style]}>
            {modules.map((m) => (
                <View key={m.id} style={styles.moduleChip}>
                    <Text style={styles.moduleLabel}>{m.label}</Text>
                </View>
            ))}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 12,
        padding: 14,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderStyle: "dashed",
    },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 11,
        color: theme.colors.primary,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 10,
    },
    subjectLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: theme.colors.grey,
        marginBottom: 6,
    },
    chipsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
    },
    moduleChip: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        paddingHorizontal: 9,
        paddingVertical: 5,
        borderRadius: 12,
    },
    moduleChipSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    moduleChipUnselected: {
        opacity: 0.55,
        backgroundColor: theme.colors.white,
        borderStyle: "dashed",
    },
    moduleLabel: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
    },
    moduleLabelUnselected: {
        color: theme.colors.light,
    },
})

export default BrevetModulesList
